/**
 * Gantt Chart simple pour le Roadmap
 */

interface Project {
  name: string;
  start: number; // Mois de début (1-12)
  duration: number; // Durée en mois
  priority: 'high' | 'medium' | 'low';
  progress: number; // 0-100
}

interface RoadmapGanttProps {
  projects: Project[];
  isQuarterly?: boolean;
}

export default function RoadmapGantt({ projects, isQuarterly = true }: RoadmapGanttProps) {
  const months = isQuarterly 
    ? ['Q1', 'Q2', 'Q3', 'Q4']
    : ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-blue-500';
    }
  };

  const getPriorityBg = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-50 border-red-200';
      case 'medium': return 'bg-yellow-50 border-yellow-200';
      case 'low': return 'bg-green-50 border-green-200';
      default: return 'bg-blue-50 border-blue-200';
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      <h3 className="text-xl font-bold text-gray-900 mb-6">
        Timeline des Projets
      </h3>

      <div className="space-y-4">
        {/* Timeline header */}
        <div className="flex gap-2">
          <div className="w-48 flex-shrink-0" /> {/* Espace pour les noms */}
          <div className="flex-1 grid grid-cols-12 gap-1">
            {months.map((month, idx) => (
              <div key={idx} className="text-center text-xs font-medium text-gray-600">
                {month}
              </div>
            ))}
          </div>
        </div>

        {/* Projets */}
        {projects.map((project, idx) => {
          const startCol = Math.ceil((project.start / 12) * 12);
          const spanCols = Math.ceil((project.duration / 12) * 12);
          
          return (
            <div key={idx} className="flex gap-2 items-center">
              {/* Nom du projet */}
              <div className="w-48 flex-shrink-0">
                <div className={`p-2 rounded-lg border ${getPriorityBg(project.priority)}`}>
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {project.name}
                  </p>
                  <p className="text-xs text-gray-600">
                    {project.duration} mois • {project.progress}%
                  </p>
                </div>
              </div>

              {/* Barre de temps */}
              <div className="flex-1 relative">
                <div className="grid grid-cols-12 gap-1 h-12 items-center">
                  {/* Barre du projet */}
                  <div
                    className={`absolute h-8 rounded-lg ${getPriorityColor(project.priority)} shadow-md flex items-center px-2 transition-all hover:shadow-lg`}
                    style={{
                      left: `${(startCol - 1) * (100 / 12)}%`,
                      width: `${spanCols * (100 / 12)}%`,
                    }}
                  >
                    {/* Progression */}
                    <div className="absolute inset-0 bg-white/30 rounded-lg overflow-hidden">
                      <div
                        className="h-full bg-white/40"
                        style={{ width: `${project.progress}%` }}
                      />
                    </div>
                    <span className="relative z-10 text-white text-xs font-bold truncate">
                      {project.progress}%
                    </span>
                  </div>

                  {/* Grille de fond */}
                  {[...Array(12)].map((_, i) => (
                    <div key={i} className="h-12 border-l border-gray-200" />
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Légende */}
      <div className="mt-6 flex gap-4 justify-center">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-red-500 rounded" />
          <span className="text-xs text-gray-600">Haute priorité</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-yellow-500 rounded" />
          <span className="text-xs text-gray-600">Moyenne priorité</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-500 rounded" />
          <span className="text-xs text-gray-600">Basse priorité</span>
        </div>
      </div>
    </div>
  );
}







